import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Add from '@mui/icons-material/Add';
import Check from '@mui/icons-material/Check';
import NumberedSection from '../selection/NumberedSection';
import { IncludeFlag, RelatedOption } from './exportTypeMeta';

interface RelatedDataOptionsProps {
  step: number;
  typeLabel: string;
  options: RelatedOption[];
  isOn: (key: IncludeFlag) => boolean;
  onToggle: (key: IncludeFlag) => void;
  onSetAll: (keys: IncludeFlag[], value: boolean) => void;
}

/** Toggle chips for the include* flags that apply to the selected export type. */
const RelatedDataOptions: React.FC<RelatedDataOptionsProps> = ({ step, typeLabel, options, isOn, onToggle, onSetAll }) => {
  const onCount = options.filter((option) => isOn(option.key)).length;
  const allOn = onCount === options.length;

  return (
    <NumberedSection
      step={step}
      title="Include related data"
      meta={`${onCount} of ${options.length} selected`}
      description={`Related records are added to the export alongside the ${typeLabel.toLowerCase()}, which are always included.`}
      action={
        <Button
          onClick={() => onSetAll(options.map((option) => option.key), !allOn)}
          sx={{ height: 32, px: 1.5, borderRadius: 4, fontSize: 13, fontWeight: 600, color: 'primary.dark', '&:hover': { bgcolor: 'brand.container' } }}
        >
          {allOn ? 'Clear all' : 'Select all'}
        </Button>
      }
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {options.map((option) => {
          const on = isOn(option.key);
          return (
            <ButtonBase
              key={option.key}
              role="checkbox"
              aria-checked={on}
              title={option.description}
              onClick={() => onToggle(option.key)}
              sx={(theme) => ({
                height: 40,
                pl: 1.25,
                pr: 1.75,
                gap: 0.75,
                borderRadius: 5,
                border: 1,
                fontSize: 14,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                borderColor: on ? 'transparent' : 'divider',
                bgcolor: on ? 'brand.container' : 'background.paper',
                color: on ? 'brand.onContainer' : 'text.secondary',
                '&:hover': { borderColor: 'primary.dark' },
                '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
              })}
            >
              {on ? <Check sx={{ fontSize: 20 }} /> : <Add sx={{ fontSize: 20 }} />}
              {option.label}
            </ButtonBase>
          );
        })}
      </Box>
    </NumberedSection>
  );
};

export default RelatedDataOptions;
